import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

interface PasswordStrengthIndicatorProps {
  password: string;
  showDetails?: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showDetails = true
}) => {
  const calculateStrength = (password: string): { score: number; label: string; color: string } => {
    if (!password) {
      return { score: 0, label: '', color: 'grey' };
    }

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password)
    };

    // Calculate score
    if (checks.length) score += 20;
    if (checks.lowercase) score += 20;
    if (checks.uppercase) score += 20;
    if (checks.number) score += 20;
    if (checks.special) score += 20;

    // Determine strength level
    let label = '';
    let color = '';

    if (score === 0) {
      label = '';
      color = 'grey';
    } else if (score <= 40) {
      label = 'Weak';
      color = '#f44336';
    } else if (score <= 60) {
      label = 'Fair';
      color = '#ff9800';
    } else if (score <= 80) {
      label = 'Good';
      color = '#ffc107';
    } else {
      label = 'Strong';
      color = '#4caf50';
    }

    return { score, label, color };
  };

  const strength = calculateStrength(password);

  if (!password) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Password Strength
        </Typography>
        {strength.label && (
          <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600 }}>
            {strength.label}
          </Typography>
        )}
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={strength.score}
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            backgroundColor: strength.color,
            borderRadius: 2
          }
        }}
      />

      {showDetails && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Requirements:
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
            <Typography component="li" variant="caption" color={password.length >= 8 ? 'success.main' : 'text.secondary'}>
              At least 8 characters
            </Typography>
            <Typography component="li" variant="caption" color={/[a-z]/.test(password) ? 'success.main' : 'text.secondary'}>
              One lowercase letter
            </Typography>
            <Typography component="li" variant="caption" color={/[A-Z]/.test(password) ? 'success.main' : 'text.secondary'}>
              One uppercase letter
            </Typography>
            <Typography component="li" variant="caption" color={/\d/.test(password) ? 'success.main' : 'text.secondary'}>
              One number
            </Typography>
            <Typography component="li" variant="caption" color={/[@$!%*?&]/.test(password) ? 'success.main' : 'text.secondary'}>
              One special character (@$!%*?&)
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator; 